import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { finalize } from 'rxjs';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Designation } from '../../employees.model';
import { DesignationService } from '../../designation.service';
import { DeleteConfirmationModalComponent } from '../../../common/components/delete-confirmation-modal/delete-confirmation-modal.component';

@Component({
  selector: 'app-designations',
  standalone: false,
  templateUrl: './designations.component.html',
  styleUrl: './designations.component.scss',
})
export class DesignationsComponent implements OnInit, OnDestroy {
  @ViewChild('designationModal') designationModalRef!: TemplateRef<any>;

  protected designations: Designation[] = [];
  protected isDataLoading = false;
  protected isSaving = false;
  protected isEditMode = false;
  protected modalRef: NgbModalRef | null = null;
  protected designationForm!: FormGroup;
  protected formError: string | null = null;

  private editingDesignationId: string | null = null;

  constructor(
    private designationService: DesignationService,
    private modalService: NgbModal,
    private fb: FormBuilder
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.loadDesignations();
  }

  private initForm(): void {
    this.designationForm = this.fb.group({
      title: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(255)]],
    });
  }

  private loadDesignations(): void {
    this.isDataLoading = true;
    this.designationService
      .list()
      .pipe(finalize(() => (this.isDataLoading = false)))
      .subscribe({
        next: (designations: Designation[]) => {
          this.designations = designations;
        },
      });
  }

  protected onAddDesignation(): void {
    this.isEditMode = false;
    this.editingDesignationId = null;
    this.formError = null;
    this.designationForm.reset({ title: '' });

    const options: NgbModalOptions = { backdrop: 'static' };
    this.modalRef = this.modalService.open(this.designationModalRef, options);
  }

  protected onEditDesignation(designation: Designation): void {
    this.isEditMode = true;
    this.editingDesignationId = designation.id;
    this.formError = null;

    this.designationForm.patchValue({ title: designation.title });

    const options: NgbModalOptions = { backdrop: 'static' };
    this.modalRef = this.modalService.open(this.designationModalRef, options);
  }

  protected onDeleteDesignation(designation: Designation): void {
    const ref = this.modalService.open(DeleteConfirmationModalComponent, {
      backdrop: 'static',
      keyboard: false,
    });

    ref.componentInstance.title = 'Delete Designation';
    ref.componentInstance.message = `Are you sure you want to delete "${designation.title}"?`;
    ref.componentInstance.warning = 'Employees holding this designation will be left without one.';

    ref.result.then(() => {
      this.designationService.delete(designation.id).subscribe({
        next: () => {
          this.designations = this.designations.filter(d => d.id !== designation.id);
        },
      });
    });
  }

  protected onSubmit(): void {
    if (this.designationForm.invalid) return;

    this.isSaving = true;
    this.formError = null;

    const payload = { title: this.designationForm.value.title.trim() };

    if (this.isEditMode && this.editingDesignationId) {
      this.designationService
        .update(this.editingDesignationId, payload)
        .pipe(finalize(() => (this.isSaving = false)))
        .subscribe({
          next: (updated: Designation) => {
            const idx = this.designations.findIndex(d => d.id === updated.id);
            if (idx !== -1) this.designations[idx] = updated;
            this.modalRef?.dismiss();
          },
          error: () => {
            this.formError = 'Failed to update designation. Please try again.';
          },
        });
    } else {
      this.designationService
        .create(payload)
        .pipe(finalize(() => (this.isSaving = false)))
        .subscribe({
          next: (created: Designation) => {
            this.designations = [...this.designations, created];
            this.modalRef?.dismiss();
          },
          error: () => {
            this.formError = 'Failed to create designation. Please try again.';
          },
        });
    }
  }

  protected isInvalid(field: string): boolean {
    const ctrl = this.designationForm.get(field);
    return (ctrl?.invalid && (ctrl.touched || ctrl.dirty)) ?? false;
  }

  ngOnDestroy(): void {
    this.modalRef?.dismiss();
  }
}
